import { ChevronDown, CircleHelp, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { FieldOption, FormField } from '@/types/form-definition';

export type FieldLogicAction = 'show' | 'hide' | 'require' | 'unrequire';
type FieldClauseOp = 'eq' | 'in';
export type FieldConditionalLogicBlock = {
  action: FieldLogicAction;
  match?: 'all' | 'any';
  when?: { fieldId: string; op: FieldClauseOp; value?: string; values?: string[] }[];
};

type Props = {
  title: string;
  description: string;
  action: FieldLogicAction;
  emptyCta: string;
  blocks: FieldConditionalLogicBlock[];
  setBlocks: (next: FieldConditionalLogicBlock[]) => void;
  fieldById: Map<string, FormField>;
  otherFieldIds: string[];
  fieldLabel: (id: string) => string;
  getFieldChoiceOptions: (fieldId: string) => FieldOption[];
};

/**
 * Module-level component so React does not treat it as a new component type on every parent render
 * (nested function components inside parents remount children and break controlled input focus).
 */
export function FieldLogicConditionsSection({
  title,
  description,
  action,
  emptyCta,
  blocks,
  setBlocks,
  fieldById,
  otherFieldIds,
  fieldLabel,
  getFieldChoiceOptions,
}: Props) {
  const sectionBlocks = blocks.filter((b) => b.action === action);
  const helpText =
    action === 'show'
      ? 'If a field has any Show rules, it starts hidden and becomes visible only when at least one Show rule matches.'
      : action === 'hide'
        ? 'Hide rules override Show rules. Use Hide for exceptions.'
        : action === 'require'
          ? 'Conditional requiredness is applied in order and only matters when the field is visible.'
          : 'Unrequire can make a required field optional when conditions match (only when visible).';

  return (
    <div className="grid gap-3 rounded-xl border border-border/60 bg-muted/10 p-3">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label="Help"
              >
                <CircleHelp className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={6}>
              {helpText}
            </TooltipContent>
          </Tooltip>
        </div>
        <p className="text-[11px] leading-snug text-muted-foreground">{description}</p>
      </div>

      {sectionBlocks.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-background/60 p-3">
          <p className="text-xs text-muted-foreground">No rules yet.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => {
              const next: FieldConditionalLogicBlock = {
                action,
                match: 'all',
                when: [{ fieldId: otherFieldIds[0] ?? '', op: 'eq' }],
              };
              setBlocks([...blocks, next]);
            }}
            disabled={otherFieldIds.length === 0}
          >
            {emptyCta}
          </Button>
          {otherFieldIds.length === 0 ? (
            <p className="mt-2 text-[11px] text-muted-foreground">Add another field first to build conditions.</p>
          ) : null}
        </div>
      ) : (
        <div className="grid gap-3">
          {blocks.map((b, globalIndex) => {
            if (b.action !== action) return null;
            const when = Array.isArray(b.when) ? b.when : [];
            const match = b.match === 'any' ? 'any' : 'all';
            return (
              <div key={`${action}-${globalIndex}`} className="rounded-lg border border-border/60 bg-background p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="capitalize">
                      {action}
                    </Badge>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <span>
                        Match <span className="font-mono text-[11px]">{match}</span>
                      </span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                            aria-label="Help for match mode"
                          >
                            <CircleHelp className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" sideOffset={6}>
                          {match === 'all'
                            ? 'All conditions must be true (AND).'
                            : 'Any condition can be true (OR).'}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select
                      value={match}
                      onValueChange={(v) => {
                        const next = [...blocks];
                        next[globalIndex] = { ...b, match: v === 'any' ? 'any' : 'all' };
                        setBlocks(next);
                      }}
                    >
                      <SelectTrigger className="h-8 w-[120px] bg-background text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">all</SelectItem>
                        <SelectItem value="any">any</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => {
                        const next = blocks.filter((_, i) => i !== globalIndex);
                        setBlocks(next);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>

                <div className="mt-3 grid gap-2">
                  {when.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Add a condition to make this rule active.</p>
                  ) : null}

                  {when.map((w, wIdx) => {
                    const sourceField = fieldById.get(w.fieldId);
                    const sourceType = sourceField?.type;
                    const ops: { value: FieldClauseOp; label: string }[] =
                      sourceType === 'singleChoice'
                        ? [
                            { value: 'eq', label: 'is' },
                            { value: 'in', label: 'is one of' },
                          ]
                        : [{ value: 'eq', label: 'equals' }];

                    const choiceOptions = sourceType === 'singleChoice' ? getFieldChoiceOptions(w.fieldId) : [];
                    const selectedValues = Array.isArray(w.values) ? w.values : [];

                    return (
                      <div
                        key={`${globalIndex}-${wIdx}`}
                        className="grid gap-2 rounded-md border border-border/60 bg-muted/5 p-2 sm:grid-cols-[1fr_140px_1fr_auto]"
                      >
                        <div className="grid gap-1">
                          <Label className="text-[11px] text-muted-foreground">When field</Label>
                          <Select
                            value={w.fieldId || ''}
                            onValueChange={(v) => {
                              const next = [...blocks];
                              const nextWhen = [...(when ?? [])];
                              nextWhen[wIdx] = { fieldId: v, op: 'eq' };
                              next[globalIndex] = { ...b, when: nextWhen };
                              setBlocks(next);
                            }}
                          >
                            <SelectTrigger className="bg-background text-xs">
                              <SelectValue placeholder="Select field" />
                            </SelectTrigger>
                            <SelectContent>
                              {otherFieldIds.map((id) => (
                                <SelectItem key={id} value={id}>
                                  {fieldLabel(id)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="grid gap-1">
                          <Label className="text-[11px] text-muted-foreground">Operator</Label>
                          <Select
                            value={w.op}
                            onValueChange={(v) => {
                              const next = [...blocks];
                              const nextWhen = [...(when ?? [])];
                              nextWhen[wIdx] =
                                v === 'in'
                                  ? { ...w, op: 'in', value: undefined, values: Array.isArray(w.values) ? w.values : [] }
                                  : { ...w, op: 'eq', values: undefined, value: w.value ?? '' };
                              next[globalIndex] = { ...b, when: nextWhen };
                              setBlocks(next);
                            }}
                          >
                            <SelectTrigger className="bg-background text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ops.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="grid gap-1">
                          <div className="flex items-center gap-1">
                            <Label className="text-[11px] text-muted-foreground">Value</Label>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex h-5 w-5 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                                  aria-label="Help for value"
                                >
                                  <CircleHelp className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" sideOffset={6}>
                                For single choice fields, logic compares the option “value” (stable id), not the label text.
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          {sourceType === 'singleChoice' ? (
                            w.op === 'in' ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button type="button" variant="outline" className="h-9 justify-between bg-background text-xs">
                                    <span className="truncate">
                                      {selectedValues.length > 0 ? `${selectedValues.length} selected` : 'Select values'}
                                    </span>
                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-64 p-2" align="start">
                                  <ScrollArea className="h-52 pr-2">
                                    <div className="grid gap-2">
                                      {choiceOptions.map((opt) => {
                                        const checked = selectedValues.includes(opt.value);
                                        return (
                                          <label key={opt.value} className="flex cursor-pointer items-center gap-2 text-xs">
                                            <Checkbox
                                              checked={checked}
                                              onCheckedChange={(c) => {
                                                const nextValues = c
                                                  ? Array.from(new Set([...selectedValues, opt.value]))
                                                  : selectedValues.filter((x) => x !== opt.value);
                                                const next = [...blocks];
                                                const nextWhen = [...(when ?? [])];
                                                nextWhen[wIdx] = { ...w, values: nextValues };
                                                next[globalIndex] = { ...b, when: nextWhen };
                                                setBlocks(next);
                                              }}
                                            />
                                            <span className="min-w-0 truncate">{opt.label || opt.value}</span>
                                            <span className="ml-auto font-mono text-[10px] text-muted-foreground">{opt.value}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </ScrollArea>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <Select
                                value={w.value ?? ''}
                                onValueChange={(v) => {
                                  const next = [...blocks];
                                  const nextWhen = [...(when ?? [])];
                                  nextWhen[wIdx] = { ...w, value: v };
                                  next[globalIndex] = { ...b, when: nextWhen };
                                  setBlocks(next);
                                }}
                              >
                                <SelectTrigger className="bg-background text-xs">
                                  <SelectValue placeholder="Select value" />
                                </SelectTrigger>
                                <SelectContent>
                                  {choiceOptions.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label || opt.value}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )
                          ) : (
                            <Input
                              value={w.value ?? ''}
                              onChange={(e) => {
                                const next = [...blocks];
                                const nextWhen = [...(when ?? [])];
                                nextWhen[wIdx] = { ...w, value: e.target.value };
                                next[globalIndex] = { ...b, when: nextWhen };
                                setBlocks(next);
                              }}
                              placeholder="Enter value"
                              className="bg-background text-xs"
                            />
                          )}
                        </div>

                        <div className="flex items-end justify-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 text-muted-foreground hover:text-foreground"
                            aria-label="Remove condition"
                            onClick={() => {
                              const next = [...blocks];
                              const nextWhen = [...(when ?? [])].filter((_, i) => i !== wIdx);
                              next[globalIndex] = { ...b, when: nextWhen };
                              setBlocks(next);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-fit"
                    onClick={() => {
                      const next = [...blocks];
                      const nextWhen = [...(when ?? [])];
                      nextWhen.push({ fieldId: otherFieldIds[0] ?? '', op: 'eq' });
                      next[globalIndex] = { ...b, when: nextWhen };
                      setBlocks(next);
                    }}
                    disabled={otherFieldIds.length === 0}
                  >
                    Add condition
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
